// ============================================================
// fondos/efectos/index.js — CÓMO se pinta cada fondo animado: id → efecto, cargado bajo demanda
//
// Cada fondo se importa con `import()` la primera vez que una sección lo pide (como siempre: en el
// editor cada uno es un chunk de `dist/chunks/`; en el publicado, esbuild lo mete en el bundle).
//
// P3 del motor de fondos (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`), dos técnicas:
//   · LIENZO (F2b): los 9 de partículas y líneas y, desde la F3 (§3.1-bis, 30-09), humo, mesh y
//     blobs; efectos con el contrato de §2.2 (`crear`, `redimensionar`, `dibujar`, `destruir?`)
//     sobre la base `fondos/lienzo.js`, en el bucle único del motor.
//   · CAPAS (F3): solo aurora, un efecto que DESCRIBE sus manchas (`disenar`) sobre la base
//     `fondos/capas.js`, animadas en el compositor. Fluido se retiró (`catalogo.js::RETIRADOS`).
// Las TEXTURAS no pasan por aquí: se pintan sin cargar nada (`fondos/texturas.js`).
// ============================================================

import { porId } from '../catalogo.js';

// Rutas LITERALES a propósito: esbuild solo trocea (y empaqueta) un `import()` cuyo destino puede
// leer al compilar.
const PROPIOS = {
    'neural': () => import('./neural.js'),
    'particles': () => import('./particles.js'),
    'waves': () => import('./waves.js'),
    'constellation': () => import('./constellation.js'),
    'geometric': () => import('./geometric.js'),
    'bokeh': () => import('./bokeh.js'),
    'matrix-rain': () => import('./matrix-rain.js'),
    'dot-grid': () => import('./dot-grid.js'),
    'topographic': () => import('./topographic.js'),
    'smoke': () => import('./smoke.js'),
    'mesh-gradient': () => import('./mesh-gradient.js'),
    'blobs-morph': () => import('./blobs-morph.js'),
};

const CAPAS = {
    'aurora': () => import('./aurora.js'),
};

/**
 * Los parámetros del catálogo que NO son del efecto sino de la capa: el color principal (viaja
 * aparte, como acento) y la opacidad (la aplica `fondos/capa.js` a la capa que pinta, sin rehacer
 * el efecto). La clave `blur` es la SUAVIDAD y la interpreta el EFECTO en todos los que la declaran
 * —el sprite de bokeh, el degradado de las manchas de los fluidos—: nada de `filter` (E10).
 */
const DE_LA_CAPA = ['color1', 'opacity'];

function _propio(id) {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(PROPIOS, id);
}

function _deCapas(id) {
    return typeof id === 'string' && Object.prototype.hasOwnProperty.call(CAPAS, id);
}

/** ¿`id` es un fondo animado que se sabe pintar? (`hasOwnProperty`: 'constructor' no lo es.) */
export function esEfecto(id) {
    return _propio(id) || _deCapas(id);
}

/**
 * Lo que el efecto `id` recibe de la config: las claves que el catálogo le declara, menos las de la
 * capa, y las numéricas dentro de su rango (`_dentroDelRango`). Sin valores por defecto: cada efecto
 * aplica los suyos cuando falta uno (neural sin `speed` va a 1×, con `speed: 5` a 1,56×), así que
 * rellenarlos cambiaría lo publicado. En los
 * lienzos es lo que le pasaba cada factoría del antiguo `BackgroundRegistry` más lo que se perdía
 * (E14: la velocidad y el segundo color de constellation; E10: la suavidad de bokeh), y los de
 * capas reciben además su densidad y su suavidad
 * (`tests/test_fondos_motor.py` lo guarda contra una tabla congelada).
 *
 * @param {string} id
 * @param {Object} [config] - la config EFECTIVA (`fondos/efectiva.js`)
 * @returns {Object} `{speed?, density?, color2?, blur?}` según el fondo
 */
export function opcionesDelEfecto(id, config) {
    const f = porId(id);
    const out = {};
    if (!f) return out;
    Object.keys(f.params).forEach(function (k) {
        if (DE_LA_CAPA.indexOf(k) !== -1) return;
        const v = config ? config[k] : undefined;
        out[k] = f.params[k].tipo === 'color' ? v : _dentroDelRango(v, f.params[k]);
    });
    return out;
}

/**
 * Un parámetro numérico, dentro del rango `{min, max}` que le da el catálogo. El panel ya guarda
 * acotado (`fondos/config.js::normalizarConfig`) y la IA también (`llm_sanitizers._sanear_bg_cfg`),
 * pero el Guardar no lo revisa (`views.api_actualizar`) y lo publicado lleva la config horneada en
 * `data-bg-config`: un `density: 1e6` por POST directo eran millones de partículas y la página de
 * ese sitio se congelaba para sus visitantes. Ausente (o null) sigue igual —el efecto pone su
 * defecto— y lo que no es un número, como ausente: `'x'` daba NaN y el fondo salía vacío. Dentro del
 * rango llega TAL CUAL (sin redondear).
 */
function _dentroDelRango(v, r) {
    if (v === undefined || v === null) return v;
    if (typeof v !== 'number' || Number.isNaN(v)) return undefined;
    return Math.max(r.min, Math.min(r.max, v));
}

/**
 * Carga el efecto de `id`.
 * @returns {Promise<{tecnica: 'lienzo'|'capas', efecto: Object}>}
 */
export function cargarEfecto(id) {
    if (_propio(id)) {
        return PROPIOS[id]().then(function (mod) { return { tecnica: 'lienzo', efecto: mod.default }; });
    }
    if (_deCapas(id)) {
        return CAPAS[id]().then(function (mod) { return { tecnica: 'capas', efecto: mod.default }; });
    }
    return Promise.reject(new Error('Fondo sin efecto: ' + id));
}
